import { useState } from "react";
import UpdateJobStages from "../UpdateJobStages";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { resolveProjectUpdateTypes } from "@contexts/projects/updateJobs";
import { isPresentationProject } from "@utils/presentationMode";
import { useTitleAnnulments } from "@components/titleAnnulments/TitleAnnulmentsContext";
import {
  brl,
  hasAnnulments,
  isPending,
  PENDING_BANNER_TEXT,
  PENDING_BANNER_TITLE,
  titleCount,
  totalsOf,
} from "@components/titleAnnulments/summary";

// Card "Títulos anulados na Ampli" (design D6), no mesmo segmento e com o mesmo desenho do
// `CostOriginSplitCard`: .card com maxWidth 520, título com "i", selo de contagem, linhas
// "A pagar ›" e "Pago ›" e o rodapé "Pago + a pagar". Clicar numa linha abre a barra lateral.
// Com a lista pendente de aplicação ganha a faixa âmbar com o botão "Atualizar financeiro".
// Lê `activeProject.titleAnnulments`; some quando não há título anulado aplicado nem pendente.

const DIVIDER = "1px solid rgba(0, 0, 0, 0.08)";
const AMBER = "#EF9F27";
const TO_PAY_COLOR = "#d9534f";
const PAID_COLOR = "#7455fb";

export const CARD_TOOLTIP =
  "Títulos do ERP que a construtora tirou da conta da Ampli. A anulação vale pelo título inteiro: " +
  "tudo o que está a pagar e tudo o que já foi pago, em todas as parcelas. " +
  "O ERP não muda, e os números só refletem a lista depois da atualização do financeiro.";

const Row = ({ color, label, value, onClick }) => (
  <div
    className="flex justify-content-space-between"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    }}
    aria-label={`Ver títulos anulados: ${label} ${brl(value)}`}
    style={{ alignItems: "center", padding: "8px 0", borderTop: DIVIDER, cursor: "pointer" }}
  >
    <span className="flex" style={{ alignItems: "center", gap: 8 }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: "inline-block", flexShrink: 0 }} />
      <span>{label}</span>
    </span>
    <span style={{ whiteSpace: "nowrap" }}>
      <strong>{brl(value)}</strong>
      <span aria-hidden style={{ marginLeft: 8, opacity: 0.5 }}>›</span>
    </span>
  </div>
);

// Botão "Atualizar financeiro" da faixa de pendência. Fica num componente próprio para que
// `useProjectActions` só seja chamado quando há pendência a resolver.
const PendingUpdate = ({ canUpdate, buildingId, projectId }) => {
  const { createUpdateJob, getUpdateJob, selectProject } = useProjectActions();
  const annulments = useTitleAnnulments();
  const [updating, setUpdating] = useState(false);
  const [update, setUpdate] = useState(null);

  const refreshFinancial = async () => {
    if (updating) return;
    setUpdating(true);
    const result = await createUpdateJob("financial");
    setUpdating(false);
    const accepted = result?.ok || (result?.status === 409 && result?.body?.job);
    setUpdate(accepted
      ? { ok: true, job: result?.body?.job ?? null }
      : {
        ok: false,
        text: result?.body?.message ?? "Não foi possível iniciar a atualização. Tente novamente.",
      });
  };

  return (
    <>
      {canUpdate && !update?.ok && (
        <button
          type="button"
          className="button button-accent small margin-top-8"
          disabled={updating}
          onClick={refreshFinancial}
        >
          Atualizar financeiro
        </button>
      )}
      {update && !update.ok && <p role="alert" className="empty-state">{update.text}</p>}
      {update?.ok && (
        <>
          {!update.job && <p className="card-label">Atualização solicitada.</p>}
          <UpdateJobStages
            initialJob={update.job}
            fetchJob={async () => {
              const res = await getUpdateJob(buildingId);
              return { ok: !!res?.ok, job: res?.body?.job ?? null };
            }}
            onTerminal={(job) => {
              // Sem recarregar a página (apagaria um lote em preenchimento na barra lateral):
              // atualiza a lista da barra e o resumo da obra pelas ações do contexto.
              if (job?.status === "succeeded") {
                annulments?.reload?.();
                if (projectId) selectProject?.(projectId);
              }
              setUpdate(null);
            }}
          />
        </>
      )}
    </>
  );
};

const TitleAnnulmentsCard = () => {
  const { activeProject, companies } = useProjectContext();
  const { openOffcanvas } = useTitleAnnulments();
  const summary = activeProject?.titleAnnulments;

  if (!hasAnnulments(summary)) return null;

  const totals = totalsOf(summary);
  const pending = isPending(summary);
  const company = companies?.find((item) => item._id === activeProject?.company_id);
  const canUpdate = !isPresentationProject(activeProject)
    && resolveProjectUpdateTypes(activeProject, company?.erp).includes("financial");

  return (
    <div className="card" style={{ maxWidth: 520, marginTop: 24, marginBottom: 24 }}>
      <div className="flex justify-content-space-between">
        <span className="card-title">Títulos anulados na Ampli</span>
        <span
          title={CARD_TOOLTIP}
          aria-label={CARD_TOOLTIP}
          style={{
            cursor: "help",
            fontSize: 12,
            width: 16,
            height: 16,
            lineHeight: "16px",
            textAlign: "center",
            borderRadius: "50%",
            border: "1px solid rgba(0,0,0,0.25)",
            color: "rgba(0,0,0,0.5)",
            flexShrink: 0,
          }}
        >
          i
        </span>
      </div>

      <p className="card-label" data-testid="title-annulments-count" style={{ margin: "4px 0 8px" }}>
        {titleCount(totals.count)}
      </p>

      <Row color={TO_PAY_COLOR} label="A pagar" value={totals.toPay} onClick={openOffcanvas} />
      <Row color={PAID_COLOR} label="Pago" value={totals.paid} onClick={openOffcanvas} />

      <div
        className="flex justify-content-space-between"
        style={{ alignItems: "center", paddingTop: 12, marginTop: 4, borderTop: DIVIDER }}
      >
        <span className="card-label">Pago + a pagar</span>
        <strong>{brl(totals.total)}</strong>
      </div>

      {pending && (
        <div
          role="status"
          data-testid="title-annulments-pending"
          style={{
            marginTop: 12,
            padding: "8px 10px",
            borderRadius: 4,
            border: `1px solid ${AMBER}`,
            background: "rgba(239, 159, 39, 0.12)",
          }}
        >
          <p style={{ fontWeight: 600 }}>{PENDING_BANNER_TITLE}</p>
          <p className="card-label">{`${PENDING_BANNER_TEXT}. Os valores ainda não refletem a lista.`}</p>
          <PendingUpdate canUpdate={canUpdate} buildingId={activeProject?.building_id} projectId={activeProject?._id} />
        </div>
      )}
    </div>
  );
};

export default TitleAnnulmentsCard;
